import { useContext, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import isObject from 'lodash/isObject'
import { SelectOption } from '@microservices/wiskey-react-components'

import { FieldsConfigContext } from '@pages/FormCreateOrEdit/components/FieldsConfig/FieldsConfig'

import { getDataFromState, getStateFromData } from '@components/CommandParameters/helpers'

import {
  useCreateActionWithIdMutation,
  useFetchAllFormQuery,
  useFetchDictionariesByCategoryCodeQuery,
  useFetchViewByIdQuery,
  useUpdateActionMutation,
} from '@redux/api'
import { useFetchContextMenuListQuery } from '@redux/api/contextMenu.api'
import { useFetchAllObjectQuery } from '@redux/api/objects.api'
import { useFetchAllTooltipsQuery } from '@redux/api/tooltips.api'

import { usePrompt, useVariables } from '@hooks'
import { getAvailableActionEvents } from '@helpers'
import { ACTION_CODE, CATEGORIES, ENTITY_TYPES_REQUEST, EVENT_CODE, MODAL_TYPE } from '@constants'
import { ActionRow, AutocompleteOption, GETAction, ModalType, Table } from '@types'

import { getDefaultValue } from '../helpers'

import { ActionForm, useInputs } from './useInputs'

export const useAddActionDialog = ({
  outerParametersId,
  entityId,
  currentRow,
  actionTable,
  modalType,
  onCloseModal,
  onUpdateActions,
}: {
  outerParametersId: number
  entityId: number
  currentRow: ActionRow | null
  actionTable: Table<GETAction>
  modalType: ModalType
  onCloseModal: (isDirty?: boolean) => void
  onUpdateActions: (data: ActionRow, modalType: ModalType) => void
}) => {
  const defaultValues: ActionForm = getDefaultValue()
  const methods = useForm<ActionForm>({ defaultValues })
  const {
    handleSubmit,
    reset,
    formState: { isDirty },
    watch,
    setValue,
  } = methods

  const { formId } = useContext(FieldsConfigContext)

  const [isOpenScriptValueDialog, setIsOpenScriptValueDialog] = useState(false)
  const [currentNameScriptDialog, setCurrentNameScriptDialog] = useState('')

  const watchJson = watch('json')
  const watchJsValue = watch('jsValue')
  const watchActionCode = watch('actionCode')
  const watchEvent = watch('eventCode')
  const watchFormObject = watch('formObject')

  const [isLoading, setLoading] = useState(true)

  const [createAction] = useCreateActionWithIdMutation()
  const [editAction] = useUpdateActionMutation()

  const { data: entity } = useFetchViewByIdQuery(
    { id: entityId },
    {
      skip: !entityId,
    }
  )
  const { data: bindingValuesAction } = useFetchDictionariesByCategoryCodeQuery(
    CATEGORIES.BINDING_VIEW_ACTION
  )

  const isOpenFormAction =
    watchActionCode === ACTION_CODE.OPEN_ANOTHER_FORM_MODEL ||
    watchActionCode === ACTION_CODE.OPEN_FORM_MODEL

  const isContextMenu = watchActionCode === ACTION_CODE.OPEN_CONTEXT_MENU
  const isTooltip = watchActionCode == ACTION_CODE.OPEN_TOOLTIP

  let categoryCode: CATEGORIES = CATEGORIES.BINDING_VIEW_EVENTS_ACTION

  if (isContextMenu) {
    categoryCode = CATEGORIES.BINDING_EVENTS_ACTION_CONTEXT_MENU
  }

  if (isTooltip) {
    categoryCode = CATEGORIES.BINDING_EVENTS_ACTION_TOOLTIP
  }

  usePrompt({ when: isDirty })

  const {
    data: bindingValuesActionEvents,
    isLoading: isLoadingBindingValuesEvents,
    isFetching: isFetchingBindingValuesEvents,
  } = useFetchDictionariesByCategoryCodeQuery(categoryCode)

  const loadingBindingValuesEvents = isLoadingBindingValuesEvents || isFetchingBindingValuesEvents
  const objectCode = entity?.objectCode ?? ''

  const {
    data: forms,
    isFetching: isFetchingAllForm,
    isLoading: isLoadingAllForm,
  } = useFetchAllFormQuery(
    {
      objectCode: (watchFormObject?.id as string) ?? '',
    },
    {
      skip: !watchFormObject,
    }
  )

  const { data: contextMenus } = useFetchContextMenuListQuery({ objectCode }, { skip: !objectCode })
  const { data: tooltips } = useFetchAllTooltipsQuery({ objectCode }, { skip: !objectCode })

  const { data: objects, isLoading: isAllObjectsLoading } = useFetchAllObjectQuery(undefined, {
    skip: !isOpenFormAction,
  })

  const formVariables = useVariables({ id: formId, type: ENTITY_TYPES_REQUEST.FORM, formId })

  const loadingAllForm = isFetchingAllForm || isLoadingAllForm

  const contextMenusOptions = useMemo(
    () => contextMenus?.map(({ id, title: label }) => ({ id, label })) || [],
    [contextMenus]
  )

  const tooltipOptions = useMemo(
    () => tooltips?.map(({ id, title: label }) => ({ id, label })) || [],
    [tooltips]
  )

  const targetColumnsOptions = useMemo(
    () => entity?.columns?.map(({ id, title: label }) => ({ id, label })) || [],
    [entity]
  )

  const formCodeOptions: AutocompleteOption[] = useMemo(
    () =>
      forms?.data.map(form => ({
        id: form.code,
        label: form.title,
      })) ?? [],
    [forms]
  )

  const filteredActionEvents = getAvailableActionEvents(
    bindingValuesActionEvents,
    actionTable,
    currentRow,
    watchActionCode
  )

  const filteredTargetColumnsOptions = targetColumnsOptions.filter(
    targetColumn =>
      !actionTable?.rows.find(
        event => event.target?.id === targetColumn.id && currentRow?.target?.id !== targetColumn.id
      )
  )

  const actionTypeOptions: SelectOption[] = useMemo(() => {
    const options =
      bindingValuesAction?.map(cat => ({
        id: cat.id,
        name: cat.code,
      })) || []

    if (!options.some(option => option.name === ACTION_CODE.PRINT_FORM_VOU)) {
      options.push({
        id: ACTION_CODE.PRINT_FORM_VOU,
        name: ACTION_CODE.PRINT_FORM_VOU,
      })
    }

    return options
  }, [bindingValuesAction])

  const variablesOptions: AutocompleteOption[] = useMemo(
    () =>
      formVariables?.map(variable => ({
        id: variable.id,
        label: variable.title,
      })) ?? [],
    [formVariables]
  )

  const eventOptions: SelectOption[] = useMemo(
    () =>
      filteredActionEvents?.map(cat => ({
        id: cat.id,
        name: cat.code,
      })) || [],
    [filteredActionEvents]
  )

  const objectOptions = useMemo(() => {
    if (!objects) {
      return []
    }

    if (watchActionCode === ACTION_CODE.OPEN_FORM_MODEL) {
      const entityObject = objects.find(object => object.code === objectCode)

      if (!entityObject) {
        return []
      }

      return [{ id: entityObject.code, label: entityObject.title }]
    }

    return objects.map(object => {
      return {
        id: object.code,
        label: object.title,
      }
    })
  }, [objects, watchActionCode])

  useEffect(() => {
    if (currentRow) {
      reset({
        ...currentRow,
        params: currentRow.params ? JSON.stringify(currentRow.params) : '',
        //order: currentRow.order?.toString(),
        formObject: currentRow?.objectCode
          ? {
              id: currentRow?.objectCode,
              label: currentRow?.objectTitle,
            }
          : null,
        objectFormCode: currentRow?.objectFormCode
          ? {
              id: currentRow?.objectFormCode,
              label: currentRow?.objectFormTitle,
            }
          : null,
        json: Boolean(currentRow.params),
        jsValue: currentRow.jsValue,
        contextMenu: currentRow.contextMenuId
          ? { id: currentRow.contextMenuId, label: currentRow.contextMenuTitle as string }
          : null,
        tooltip: currentRow.objectTooltipId
          ? { id: currentRow.objectTooltipId, label: currentRow.objectTooltipTitle }
          : null,
        targetColumn: currentRow.target
          ? { id: currentRow.target.id, label: currentRow.target.title }
          : null,
        columnToSort: null,
        variable: currentRow.variable
          ? { id: currentRow.variable.id, label: currentRow.variable.title }
          : null,
        command: currentRow.command
          ? [
              {
                ...currentRow.command,
                name: {
                  id: currentRow.command.name,
                  label: currentRow.command.name,
                },
                sourceCommand: {
                  id: currentRow.command.objectCode,
                  label: currentRow.command.objectTitle,
                },
                sourceType: {
                  id: currentRow.command.resource,
                  label: currentRow.command.resource,
                },
                type: null,
                parameters: getStateFromData(currentRow.command.parameters),
                body: getStateFromData(currentRow.command.body),
              },
            ]
          : null,
      })

      setLoading(false)
    }
  }, [currentRow])

  const handleSave = handleSubmit((data: ActionForm) => {
    const formToLcConnectionId = outerParametersId
    const {
      id = Date.now(),
      code,
      title,
      actionCode = ACTION_CODE.OPEN_FORM_MODEL,
      eventCode = EVENT_CODE.ON_ROW_CLICK,
      params = '',
      formObject,
      objectFormCode,
      json,
      contextMenu,
      tooltip,
      targetColumn,
      variable,
      command,
    } = data

    if (modalType === MODAL_TYPE.EDIT && currentRow) {
      editAction?.({
        id,
        code,
        title,
        eventCode,
        actionCode,
        formToLcConnectionId,
        targetId: targetColumn?.id,
        contextMenuId: contextMenu?.id,
        tooltipId: tooltip?.id,
        variableId: variable?.id,
        command: command?.length
          ? {
              ...command[0],
              objectCode: command[0].sourceCommand?.id,
              resource: command[0].sourceType?.id,
              name: command[0].name?.id,
              objectTitle: command[0].sourceCommand?.label,
              type: command[0].type?.id,
              parameters: getDataFromState(command[0].parameters),
              body: getDataFromState(command[0].body),
            }
          : null,
        jsValue: watchJsValue || undefined,
        ...(json
          ? { params }
          : {
              objectCode:
                isObject(formObject) && formObject?.id ? formObject.id.toString() : undefined,
              objectTitle:
                isObject(formObject) && formObject?.label ? formObject.label.toString() : undefined,
              objectFormCode:
                isObject(objectFormCode) && objectFormCode?.id
                  ? objectFormCode.id.toString()
                  : undefined,
            }),
      })
        .unwrap()
        .then(data => {
          onUpdateActions(data, modalType)
          onCloseModal()
        })
        .catch(console.error)

      return
    }
    createAction?.({
      code,
      title,
      eventCode,
      actionCode,
      formToLcConnectionId,
      targetId: targetColumn?.id,
      contextMenuId: contextMenu?.id,
      tooltipId: tooltip?.id,
      variableId: variable?.id,
      command: command?.length
        ? {
            ...command[0],
            name: command[0].name?.id,
            objectTitle: command[0].sourceCommand?.label,
            type: command[0].type?.id,
            objectCode: command[0].sourceCommand?.id,
            resource: command[0].sourceType?.id,
            parameters: getDataFromState(command[0].parameters),
            body: getDataFromState(command[0].body),
          }
        : null,
      jsValue: watchJsValue || undefined,
      ...(json
        ? { params }
        : {
            objectCode:
              isObject(formObject) && formObject?.id ? formObject.id.toString() : undefined,
            objectTitle:
              isObject(formObject) && formObject?.label ? formObject.label.toString() : undefined,
            objectFormCode:
              isObject(objectFormCode) && objectFormCode?.id
                ? objectFormCode.id.toString()
                : undefined,
          }),
    })
      .unwrap()
      .then(data => {
        onUpdateActions(data, modalType)
        onCloseModal()
        reset(defaultValues)
      })
      .catch(console.error)
  })

  const actionInputs = useInputs({
    setValue,
    actionTypeOptions,
    eventOptions,
    objectOptions,
    watchJson,
    watchEvent,
    watchActionCode,
    watchFormObject,
    formCodeOptions,
    loadingAllForm,
    targetColumnsOptions: filteredTargetColumnsOptions,
    contextMenusOptions,
    variablesOptions,
    tooltipOptions,
    loadingBindingValuesEvents,
    isAllObjectsLoading,
    isOpenFormAction,
    modalType,
    setIsOpenScriptValueDialog,
    setCurrentNameScriptDialog,
  })

  return {
    objectCode,
    isDirty,
    defaultValues,
    actionInputs,
    methods,
    isLoading,
    handleSave,
    reset,
    isOpenScriptValueDialog,
    setIsOpenScriptValueDialog,
    currentNameScriptDialog,
  }
}
